import { RootStore } from './root.store';
import { makeAutoObservable } from 'mobx';
import { AddressType, ErrorType, IAddress } from '../domains/common.domain';
import { AddressFormat } from '../domains/tenant-config';
import { IQuoteAddress } from '../domains/quote.domain';
import { ICountry } from '../domains/tenant.domain';

export class AddressStore {
  root: RootStore;
  private _address: IAddress = {} as IAddress;

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.resetAddress();
    this.root = root;
  }

  get address(): IAddress {
    return this._address;
  }

  resetAddress() {
    this._address = {
      address: {
        value: '',
        errorType: 'required',
      },
      secondaryAddress: {
        value: '',
        errorType: undefined,
      },
      neighborhood: {
        value: '',
        errorType: undefined,
      },
      streetNumber: {
        value: '',
        errorType: undefined,
      },
      floor: {
        value: '',
        errorType: undefined,
      },
      apartment: {
        value: '',
        errorType: undefined,
      },
      withoutNumber: {
        value: false,
        errorType: undefined,
      },
      recipient: {
        value: this._address?.recipient?.value ?? '',
        errorType: undefined,
      },
      city: {
        value: '',
        errorType: 'required',
      },
      state: {
        value: '',
        errorType: 'required',
      },
      zipCode: {
        value: '',
        errorType: 'required',
      },
      country: {
        value: this._address?.country?.value ?? '',
        errorType: undefined,
      },
      autocompleteShortAddress: {
        value: '',
        errorType: undefined,
      },
      autocompleteInputAddress: {
        value: '',
        errorType: undefined,
      },
      autocompleteSelectedAddress: {
        value: '',
        errorType: undefined,
      },
    };
  }

  changeAddress(address: IAddress) {
    this._address = { ...address };
  }

  updateAddress(field: keyof IAddress, value: string) {
    const { useAddressAutocomplete } = this.root.tenantStore.config;
    if (field === 'withoutNumber') {
      this._address.withoutNumber = {
        value: !this._address.withoutNumber.value,
        errorType: undefined,
      };
      this._address.streetNumber = {
        value: '',
        errorType: this._address.withoutNumber.value ? undefined : 'required',
      };
      return;
    }

    this._address[field] = {
      value: value,
      errorType: !value ? 'required' : undefined,
    };

    if (!value) return;

    if (field === 'address' && value && useAddressAutocomplete) {
      const regEx = /\d/;
      if (!regEx.test(value)) {
        this._address.address.errorType = 'digits';
      }
    }

    if (field === 'zipCode') {
      if (this.selectedCountry.isZipDigitsOnly) {
        value = value.replace(/\D/g, '');
        this._address.zipCode.value = value;
      }

      this.validateZipCode(value);
    }

    if (field === 'country') {
      this.validateZipCode();
    }
  }

  private validateZipCode(value: string = this._address.zipCode.value) {
    this._address.zipCode.errorType = !this._address.zipCode.value ? 'required' : undefined;
    const digitsRegEx = /^\d*$/;
    if (this.selectedCountry.isZipDigitsOnly && !digitsRegEx.test(value)) {
      this._address.zipCode.errorType = 'digits';
    }

    if (value.length < this.selectedCountry.minZipLength || value.length > this.selectedCountry.maxZipLength) {
      this._address.zipCode.errorType = 'invalid';
    }
  }

  get selectedCountry(): ICountry {
    const { minZipLength, maxZipLength, isZipDigitsOnly } = this.root.tenantStore.config;
    const selectedCountry = this.root.tenantStore.countries.find(country => country.code === this._address.country.value);
    const country = {
      minZipLength: minZipLength,
      maxZipLength: maxZipLength,
      isZipDigitsOnly: isZipDigitsOnly,
    } as ICountry;

    return {
      ...country,
      ...selectedCountry,
    };
  }

  setError(field: keyof IAddress, errorType: ErrorType) {
    this._address[field].errorType = errorType;
  }

  get isAddressValid(): boolean {
    const format = this.root.tenantStore.config.addressFormat;
    if (format === AddressFormat.cep || format === AddressFormat.street) {
      return (
        !this._address.address.errorType &&
        !this._address.streetNumber.errorType &&
        !this._address.neighborhood.errorType &&
        !this._address.city.errorType &&
        !this._address.state.errorType &&
        !this._address.zipCode.errorType &&
        !this._address.country.errorType
      );
    }
    return (
      !this._address.address.errorType &&
      !this._address.city.errorType &&
      !this._address.state.errorType &&
      !this._address.zipCode.errorType &&
      !this._address.country.errorType
    );
  }

  init(type: AddressType): void {
    const { populateServiceInfo, populateShippingInfo, populateBillingInfo, country } = this.root.tenantStore.config;

    switch (type) {
      case 'service':
        if (populateServiceInfo) {
          this.setAddress(country, this.root.quoteStore.quoteInfo?.customer?.address);
        } else {
          this.updateAddress('country', country);
        }
        break;
      case 'shipping':
        this.updateAddress(
          'recipient',
          this.root.quoteStore.quoteInfo?.shipping?.address?.recipient ?? this.root.quoteStore.quoteInfo?.customer?.name ?? '',
        );

        if (populateShippingInfo) {
          this.setAddress(country, this.root.quoteStore.quoteInfo?.shipping?.address);
        } else {
          this.updateAddress('country', country);
        }
        break;
      case 'billing':
        if (populateBillingInfo) {
          this.setAddress(country, this.root.quoteStore.quoteInfo?.customer?.address);
        } else {
          this.updateAddress('country', this.root.quoteStore.quoteInfo?.customer?.address?.country ?? country);
        }
        break;
    }
  }

  private setAddress(defaultCountry: string, address?: IQuoteAddress) {
    this.updateAddress('address', address?.line1 ?? '');
    this.updateAddress('secondaryAddress', address?.line2 ?? '');
    this.updateAddress('neighborhood', address?.neighborhood ?? '');
    this.updateAddress('streetNumber', address?.streetNumber ?? '');
    this.updateAddress('floor', address?.line2 ?? '');
    this.updateAddress('apartment', address?.line3 ?? '');
    this.updateAddress('city', address?.city ?? '');
    this.updateAddress('state', address?.state ?? '');
    this.updateAddress('zipCode', address?.zip ?? '');
    this.updateAddress('country', address?.country ?? defaultCountry);
    if (!address?.streetNumber) {
      this.updateAddress('withoutNumber', '');
    }
  }
}
